import { useMemo } from 'react';
import { Select } from '@/isomorphic/components/forms/components/select';
import { StepCommonProps } from '../types';

type YearMonthSelectProps = Pick<StepCommonProps, 'fields' | 'updateFields'> & {
    labels: {
        title: string;
        year: string;
        month: string;
        infoTextMonth?: string;
        infoTextYear?: string;
    };
    future?: boolean;
    showRequiredError?: boolean;
};

const buildYearOptions = (placeholder: string, future = false) => {
    if (future) {
        const now = new Date().getFullYear();
        const years = Array.from({ length: 31 }, (_, i) => now + i);
        return [{ value: '', label: placeholder }, ...years.map(y => ({ value: `${y}`, label: `${y}` }))];
    }

    const now = new Date().getFullYear();
    const start = 1950;
    const years = Array.from({ length: now - start + 1 }, (_, i) => now - i);
    return [{ value: '', label: placeholder }, ...years.map(y => ({ value: `${y}`, label: `${y}` }))];
};

const buildMonthOptions = (placeholder: string) => [
    { value: '', label: placeholder },
    ...Array.from({ length: 12 }, (_, i) => {
        const month = String(i + 1).padStart(2, '0');
        return { value: month, label: month };
    }),
];

export const YearMonthSelect = ({ labels, fields, updateFields, future = false, showRequiredError = false }: YearMonthSelectProps) => {
    const yearOptions = useMemo(() => buildYearOptions(labels.year, future), [labels.year, future]);
    const monthOptions = useMemo(() => buildMonthOptions(labels.month), [labels.month]);
    const yearKey = useMemo(() => (future ? 'applicantEmploymentYearUntil' : 'applicantEmploymentYearFrom'), [future]);
    const monthKey = useMemo(() => (future ? 'applicantEmploymentMonthUntil' : 'applicantEmploymentMonthFrom'), [future]);

    const onYearChange = (value: string | number) => updateFields({ [yearKey]: String(value) });
    const onMonthChange = (value: string | number) => updateFields({ [monthKey]: String(value) });

    return (
        <>
            <p>{`${labels.title} *`}</p>
            <div className="r-flex r-gap-2">
                <div className="r-flex-1">
                    <Select
                        label={labels.year}
                        value={fields[yearKey]}
                        options={yearOptions}
                        onChange={onYearChange}
                        infoText={labels.infoTextYear}
                        showRequiredError={showRequiredError && !fields[yearKey]}
                    />
                </div>
                <div className="r-flex-1">
                    <Select
                        label={labels.month}
                        value={fields[monthKey]}
                        options={monthOptions}
                        onChange={onMonthChange}
                        infoText={labels.infoTextMonth}
                        showRequiredError={showRequiredError && !fields[monthKey]}
                    />
                </div>
            </div>
        </>
    );
};
